import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ApiError, NetworkError } from "../api/client";
import { consumeMfaRecovery } from "../api/mfaRecovery";
import { friendlyErrorMessage } from "../api/errors";
import { Button } from "../components/Button";
import { StatusBanner } from "../components/StatusBanner";

const SUCCESS_REDIRECT_DELAY = 900;

type ViewState =
  | "idle"
  | "consuming"
  | "success"
  | "expired"
  | "conflict"
  | "reconciliation_required"
  | "session_invalid"
  | "error";

function viewForError(error: unknown): ViewState {
  if (!(error instanceof ApiError)) return "error";
  if (error.status === 401) return "session_invalid";
  if (error.status === 409 && error.code === "recovery_expired") return "expired";
  if (error.status === 409) return "conflict";
  if (error.status === 502 && error.code === "reconciliation_required") return "reconciliation_required";
  return "error";
}

function messageForError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Sua sessão expirou. Faça login novamente.";
    if (error.status === 409 && error.code === "recovery_expired") return "A autorização expirou antes da conclusão.";
    if (error.status === 409) return "Esta autorização já foi processada ou não está mais disponível.";
    if (error.status === 502 && error.code === "reconciliation_required") return "A recuperação precisa de reconciliação institucional antes de uma nova tentativa.";
    return "Não foi possível concluir a recuperação agora. Tente novamente.";
  }
  if (error instanceof NetworkError) return friendlyErrorMessage(error);
  return "Não foi possível concluir a recuperação agora. Tente novamente.";
}

function TerminalPanel({ view, error, onRetry, onLogin }: {
  view: ViewState;
  error: string | null;
  onRetry: () => void;
  onLogin: () => void;
}) {
  if (view === "success") {
    return (
      <section className="mfa-recovery-enrollment__terminal" aria-labelledby="mfa-recovery-enrollment-success-title">
        <StatusBanner tone="success">Recuperação concluída.</StatusBanner>
        <h2 id="mfa-recovery-enrollment-success-title">Sua conta está pronta para um novo login</h2>
        <p className="form-card__note">Os fatores antigos foram removidos. Sua sessão será encerrada agora. Entre novamente com sua senha para cadastrar o novo autenticador.</p>
      </section>
    );
  }

  if (view === "expired") {
    return (
      <section className="mfa-recovery-enrollment__terminal" aria-labelledby="mfa-recovery-enrollment-expired-title">
        <StatusBanner tone="warning">A autorização expirou.</StatusBanner>
        <h2 id="mfa-recovery-enrollment-expired-title">Recuperação não disponível</h2>
        <p className="form-card__note">Peça à Secretaria e DAF que registre uma nova solicitação presencial.</p>
        <Button type="button" variant="secondary" onClick={onLogin}>Ir para o login</Button>
      </section>
    );
  }

  if (view === "conflict") {
    return (
      <section className="mfa-recovery-enrollment__terminal" aria-labelledby="mfa-recovery-enrollment-conflict-title">
        <StatusBanner tone="warning">A autorização não está mais disponível.</StatusBanner>
        <h2 id="mfa-recovery-enrollment-conflict-title">Recuperação já processada</h2>
        <p className="form-card__note">Esta operação não pode ser repetida. Se ainda precisar de acesso, procure a Secretaria e DAF.</p>
        <Button type="button" variant="secondary" onClick={onLogin}>Ir para o login</Button>
      </section>
    );
  }

  if (view === "session_invalid") {
    return (
      <section className="mfa-recovery-enrollment__terminal" aria-labelledby="mfa-recovery-enrollment-session-title">
        <StatusBanner tone="warning">Sua sessão não está mais disponível.</StatusBanner>
        <h2 id="mfa-recovery-enrollment-session-title">Faça login novamente</h2>
        <p className="form-card__note">A recuperação não foi iniciada com uma sessão válida. Entre novamente para continuar.</p>
        <Button type="button" variant="secondary" onClick={onLogin}>Ir para o login</Button>
      </section>
    );
  }

  if (view === "reconciliation_required") {
    return (
      <section className="mfa-recovery-enrollment__terminal" aria-labelledby="mfa-recovery-enrollment-reconciliation-title">
        <StatusBanner tone="error">A recuperação precisa de conferência institucional.</StatusBanner>
        <h2 id="mfa-recovery-enrollment-reconciliation-title">Não tente repetir automaticamente</h2>
        <p className="form-card__note">A operação pode ter alterado parte do acesso. Procure a Secretaria e DAF antes de qualquer nova tentativa.</p>
        <Button type="button" variant="secondary" onClick={onLogin}>Ir para o login</Button>
      </section>
    );
  }

  if (view === "error") {
    return (
      <section className="mfa-recovery-enrollment__terminal" aria-labelledby="mfa-recovery-enrollment-error-title">
        <StatusBanner tone="error">{error ?? "Não foi possível concluir a recuperação."}</StatusBanner>
        <h2 id="mfa-recovery-enrollment-error-title">Tente novamente</h2>
        <p className="form-card__note">A operação não será repetida sem sua confirmação.</p>
        <Button type="button" variant="secondary" onClick={onRetry}>Tentar novamente</Button>
      </section>
    );
  }

  return null;
}

function EnrollmentContent({
  view,
  error,
  onConsume,
  onRetry,
  onLogin,
}: {
  view: ViewState;
  error: string | null;
  onConsume: () => void;
  onRetry: () => void;
  onLogin: () => void;
}) {
  if (view === "idle") {
    return (
      <>
        <div className="mfa-recovery-enrollment__notice">
          <strong>Antes de continuar</strong>
          <p>Depois da confirmação, faça login novamente com sua senha. A matrícula do novo autenticador acontecerá no fluxo padrão da sua conta.</p>
        </div>
        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={onLogin}>Cancelar</Button>
          <Button type="button" onClick={onConsume}>Concluir recuperação</Button>
        </div>
      </>
    );
  }
  if (view === "consuming") {
    return <p className="mfa-recovery-enrollment__progress" role="status">Concluindo a recuperação com segurança…</p>;
  }
  return <TerminalPanel view={view} error={error} onRetry={onRetry} onLogin={onLogin} />;
}

export function MfaRecoveryEnrollmentPage() {
  const { state, logout, expireSession } = useAuth();
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [view, setView] = useState<ViewState>("idle");
  const [error, setError] = useState<string | null>(null);
  const accessToken = state.status === "authenticated" ? state.accessToken : null;

  useEffect(() => {
    if (view !== "success") return;
    const timer = window.setTimeout(() => {
      logout();
      navigate("/login", { replace: true });
    }, SUCCESS_REDIRECT_DELAY);
    return () => window.clearTimeout(timer);
  }, [logout, navigate, view]);

  useEffect(() => {
    if (view !== "idle") headingRef.current?.focus();
  }, [view]);

  function goToLogin() {
    logout();
    navigate("/login", { replace: true });
  }

  async function consume() {
    if (view === "consuming" || view === "success" || !accessToken) return;
    setError(null);
    setView("consuming");
    try {
      await consumeMfaRecovery(accessToken);
      setView("success");
    } catch (cause) {
      const nextView = viewForError(cause);
      setError(messageForError(cause));
      setView(nextView);
      if (nextView === "session_invalid") expireSession();
    }
  }

  if (state.status !== "authenticated") return null;

  return (
    <main className="centered-page mfa-recovery-enrollment-page">
      <section className="form-card mfa-recovery-enrollment-card" aria-labelledby="mfa-recovery-enrollment-title">
        <p className="mfa-challenge__eyebrow">Recuperação excepcional</p>
        <h1 id="mfa-recovery-enrollment-title" ref={headingRef} tabIndex={-1}>Concluir recuperação do autenticador</h1>
        <p className="form-card__intro">A aprovação institucional foi concluída. Ao continuar, os fatores antigos serão invalidados e sua sessão será encerrada para proteger a conta.</p>
        <EnrollmentContent
          view={view}
          error={error}
          onConsume={() => void consume()}
          onRetry={() => void consume()}
          onLogin={goToLogin}
        />
      </section>
    </main>
  );
}
